/**
 * Carrier Cutoffs Page
 * Manage per-carrier daily schedule times (returns arrival, collection window, final cutoff).
 * Carrier list comes live from Core DB; unconfigured carriers show "Not configured".
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Truck, Pencil, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Card, CardContent, Badge } from '../components/ui';
import CutoffFormModal from '../components/carrierCutoffs/CutoffFormModal';
import * as carrierCutoffsApi from '../services/carrierCutoffs.api';
import type { MergedCarrierCutoff, SetCarrierCutoffInput } from '../services/carrierCutoffs.api';
import { getCutoffStatus, getCutoffBadgeVariant } from '../utils/carrierCutoffStatus';
import { useCan } from '../contexts/useAuth';

export default function CarrierCutoffs() {
  const can = useCan();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<MergedCarrierCutoff | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  // Tick every 30s so countdowns stay fresh (no API polling - times are static per day)
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const { data: carriers, isLoading, isError, refetch } = useQuery({
    queryKey: ['carrierCutoffs'],
    queryFn: () => carrierCutoffsApi.getCarrierCutoffs(),
  });

  const saveMutation = useMutation({
    mutationFn: ({ carrierId, input }: { carrierId: number; input: SetCarrierCutoffInput }) =>
      carrierCutoffsApi.setCarrierCutoff(carrierId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carrierCutoffs'] });
      setEditing(null);
      setMutationError(null);
    },
    onError: (error: { response?: { data?: { error?: string } } }) => {
      setMutationError(error.response?.data?.error || 'Failed to save cutoff');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (carrierId: number) => carrierCutoffsApi.deleteCarrierCutoff(carrierId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['carrierCutoffs'] }),
  });

  const handleClear = (carrier: MergedCarrierCutoff) => {
    if (window.confirm(`Clear cutoff times for "${carrier.carrier_title}"?`)) {
      deleteMutation.mutate(carrier.carrier_id);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-6 h-6 text-blue-600" />
            Carrier Cutoffs
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Daily collection deadlines per carrier — all times in Baghdad (Asia/Baghdad)
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {isLoading && <p className="text-gray-500">Loading carriers…</p>}
      {isError && <p className="text-red-600">Failed to load carriers. Try refreshing.</p>}

      {carriers && (
        <Card padding="none">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3">Carrier</th>
                  <th className="px-4 py-3">Returns arrival</th>
                  <th className="px-4 py-3">Collection start</th>
                  <th className="px-4 py-3">Final cutoff</th>
                  <th className="px-4 py-3">Today's status</th>
                  <th className="px-4 py-3">Notes</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {carriers.map((carrier) => {
                  const status = getCutoffStatus(carrier.cutoff?.cutoff_time ?? null, now);
                  return (
                    <tr
                      key={carrier.carrier_id}
                      className="border-b border-gray-50 hover:bg-gray-50"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 flex items-center gap-1.5">
                          {carrier.carrier_title}
                          {carrier.core_missing && (
                            <span title="This carrier no longer exists in Core">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{carrier.carrier_slug}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {carrier.cutoff?.returns_arrival_time || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {carrier.cutoff?.collection_start_time || '—'}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900">
                        {carrier.cutoff?.cutoff_time || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={getCutoffBadgeVariant(status.state)}>{status.label}</Badge>
                      </td>
                      <td
                        className="px-4 py-3 text-gray-500 max-w-[240px] truncate"
                        title={carrier.cutoff?.notes}
                      >
                        {carrier.cutoff?.notes || ''}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {can('cutoffs.manage') && (
                            <button
                              onClick={() => {
                                setMutationError(null);
                                setEditing(carrier);
                              }}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                              title={carrier.cutoff ? 'Edit cutoff times' : 'Set cutoff times'}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {carrier.cutoff && can('cutoffs.manage') && (
                            <button
                              onClick={() => handleClear(carrier)}
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                              title="Clear cutoff times"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {carriers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No carriers found. Check the Core DB connection or run the cutoff seed script.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {editing && (
        <CutoffFormModal
          carrier={editing}
          isSaving={saveMutation.isPending}
          error={mutationError}
          onClose={() => setEditing(null)}
          onSave={(carrierId, input) => saveMutation.mutate({ carrierId, input })}
        />
      )}
    </div>
  );
}
